import type { ILayer, IRenderer, IRing } from '@/lib/CanvasEngine/backend/IRenderer'

/**
 * Balloon ripple 애니메이션을 관리하는 클래스
 */
export class RippleManager {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private entity: any // Reference to the BaseBalloonExtension instance

  // 파동 애니메이션 관련 프로퍼티
  protected rippleAnimations: IRing[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected rippleAnimationId: any
  protected rippleStartTime: number = 0
  protected rippleFollowMode: boolean = false // true: 따라다님, false: 껐다켰다(기본값)
  // 파동을 보이는 주기 수 — 끝없이 돌리면 선택 중 시스템 레이어를 초당 60회 다시 그려 A21s에서 앱+WebView CPU 약 220%를 쓴다
  protected rippleCycles: number = 2

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(entity: any) {
    this.entity = entity
  }

  private get renderer(): IRenderer {
    return this.entity.canvasEngine.getRenderer()
  }

  private getSystemLayer(): ILayer | undefined {
    return this.renderer.layers.system
  }

  /**
   * 파동 애니메이션 모드 설정
   */
  public setRippleFollowMode(followMode: boolean): void {
    this.rippleFollowMode = followMode
  }

  /**
   * 파동 애니메이션 모드 조회
   */
  public getRippleFollowMode(): boolean {
    return this.rippleFollowMode
  }

  /**
   * shape 선택 시 balloon 중앙에서 시작하는 파동 효과 애니메이션을 추가하는 함수
   */
  public addShapeAnimation(): void {
    if (!this.entity.balloon) return

    // 기존 애니메이션이 있다면 정리
    this.removeShapeAnimation()

    const systemLayer = this.getSystemLayer()
    if (!systemLayer) return

    // 루프의 선택 해제 감지(!isSelected → 정리)에 기대지 않는다 — 비선택 상태 호출(드래그 끝 등)은 바로 무시
    if (!this.entity.isSelected) return

    const balloonPos = this.entity.balloon.getPosition()
    const balloonCenter = {
      x: this.entity.node.x() + balloonPos.x,
      y: this.entity.node.y() + balloonPos.y,
    }

    // 파동 원들을 생성 (총 3개의 파동) - Ring 사용하여 가운데 투명
    const rippleCount = 3

    for (let i = 0; i < rippleCount; i++) {
      const initialColor = this.entity.getColor()
      const balloonRadius = this.entity.getBalloonSize() / 2 // balloon 반지름
      const initialOuterRadius =
        i === 0 ? Math.max(this.entity.getBalloonSize() * 0.4, balloonRadius) : balloonRadius // 첫 번째 파동만 즉시 보임

      const ripple = this.renderer.createRing({
        x: balloonCenter.x,
        y: balloonCenter.y,
        innerRadius: balloonRadius, // 가운데는 balloon 크기만큼 투명
        outerRadius: initialOuterRadius,
        fill: this.colorToRgba(initialColor, i === 0 ? 0.4 : 0), // 첫 번째 파동만 초기 투명도
        stroke: 'transparent', // 경계선 투명
        strokeWidth: 0, // 경계선 제거
        opacity: 1, // opacity는 fill에서 제어
        listening: false, // 클릭 이벤트 차단
      })

      this.rippleAnimations.push(ripple)
      systemLayer.add(ripple)

      // balloon 바로 아래에 배치
      if (this.entity.balloon) {
        // 리플은 무조건 0
        ripple.zIndex(0)
      }
    }

    // balloon을 파동보다 위에 배치하고 파동은 그 아래에 고정
    this.ensureBalloonAboveRipples()

    // 애니메이션 시작 시간 기록
    this.rippleStartTime = Date.now()

    // 애니메이션 루프 시작
    this.startRippleAnimation()
  }

  /**
   * 파동 애니메이션 루프를 시작하는 함수
   */
  private startRippleAnimation(): void {
    if (this.rippleAnimations.length === 0) return

    const animate = (): void => {
      if (this.rippleAnimations.length === 0 || !this.entity.isSelected) {
        this.removeShapeAnimation()
        return
      }

      const now = Date.now()
      const elapsed = now - this.rippleStartTime
      const rippleDuration = 2800 // 2.8초 사이클
      const _rippleDelay = rippleDuration / this.rippleAnimations.length

      // 마지막(가장 늦게 시작한) 파동까지 정해진 주기를 다 돌면 링을 지우고 루프를 멈춘다
      if (
        elapsed >=
        this.rippleCycles * rippleDuration + (this.rippleAnimations.length - 1) * _rippleDelay
      ) {
        this.removeShapeAnimation()
        return
      }

      this.rippleAnimations.forEach((ripple: IRing, index: number) => {
        // 주기를 다 돈 파동은 새 주기를 시작하지 않는다(다른 파동이 끝날 때까지 숨김)
        if (elapsed - index * _rippleDelay >= this.rippleCycles * rippleDuration) {
          ripple.opacity(0)
          ripple.outerRadius(ripple.innerRadius())
          return
        }
        // 각 파동마다 시간 지연 적용
        const rippleElapsed = (elapsed - index * _rippleDelay) % rippleDuration

        if (rippleElapsed < 0) {
          // 아직 시작 시간이 되지 않음
          ripple.opacity(0)
          ripple.outerRadius(ripple.innerRadius()) // outerRadius를 innerRadius와 같게 설정하여 보이지 않게 함
          return
        }

        // 애니메이션 진행률 (0~1)
        const progress = rippleElapsed / rippleDuration

        if (progress >= 1) {
          // 사이클 완료, 다시 시작
          ripple.opacity(0)
          ripple.outerRadius(ripple.innerRadius()) // outerRadius를 innerRadius와 같게 설정하여 보이지 않게 함
          return
        }

        // 파동 크기와 투명도 계산
        const balloonRadius = this.entity.getBalloonSize() / 2 // balloon 반지름
        const maxRadius = this.entity.getBalloonSize() * 0.83 // balloon 크기의 0.83배까지 확장
        const minRadius = this.entity.getBalloonSize() * 0.45 // balloon 크기의 45%에서 시작
        const outerRadius = minRadius + (maxRadius - minRadius) * progress

        // 투명도: 초기에는 서서히 나타나다가 나중에는 빠르게 사라짐
        let fillOpacity: number

        if (progress < 0.1) {
          // 처음 10%에서 서서히 나타남
          fillOpacity = (progress / 0.1) * 0.6
        } else if (progress < 0.5) {
          // 50%까지는 유지
          fillOpacity = 0.5
        } else {
          // 마지막 50%에서 빠르게 사라짐
          const fadeProgress = (progress - 0.5) / 0.5
          fillOpacity = 0.5 * (1 - fadeProgress)
        }

        // 파동 링 속성 업데이트
        ripple.innerRadius(balloonRadius) // 가운데는 항상 balloon 크기만큼 투명
        ripple.outerRadius(outerRadius)
        ripple.strokeWidth(0) // 경계선 제거
        ripple.stroke('transparent') // 경계선 투명
        ripple.opacity(1) // opacity는 항상 1로 유지, fill의 alpha로 투명도 제어

        // 파동 gradient 효과 (shape.color 기반으로 점점 옅어지는 색상)
        const currentColor = this.entity.getColor() // 실시간으로 현재 색상 가져오기
        const fillColor = this.colorToRgba(currentColor, Math.max(0, fillOpacity))

        ripple.fill(fillColor)
      })

      // systemLayer 다시 그리기
      const systemLayer = this.getSystemLayer()
      if (systemLayer) {
        systemLayer.batchDraw()
      }

      // 다음 프레임 예약
      this.rippleAnimationId = requestAnimationFrame(animate)
    }

    // 애니메이션 시작
    this.rippleAnimationId = requestAnimationFrame(animate)
  }

  /**
   * 색상을 rgba 형식으로 변환하는 헬퍼 함수 (hex 및 rgba 모두 지원)
   * @param color - 색상값 (#RRGGBB, #RGB 또는 rgba(r,g,b,a))
   * @param alpha - 투명도 값 (0-1)
   * @returns rgba 색상 문자열
   */
  private colorToRgba(color: string, alpha: number): string {
    let r: number, g: number, b: number

    // 이미 rgba 형식인 경우 파싱
    if (color.startsWith('rgba(')) {
      const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/)
      if (rgbaMatch) {
        r = parseInt(rgbaMatch[1], 10)
        g = parseInt(rgbaMatch[2], 10)
        b = parseInt(rgbaMatch[3], 10)
      } else {
        // rgba 파싱 실패 시 기본값
        r = g = b = 0
      }
    }
    // rgb 형식인 경우 파싱
    else if (color.startsWith('rgb(')) {
      const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10)
        g = parseInt(rgbMatch[2], 10)
        b = parseInt(rgbMatch[3], 10)
      } else {
        // rgb 파싱 실패 시 기본값
        r = g = b = 0
      }
    }
    // hex 형식인 경우 파싱
    else {
      const cleanHex = color.replace('#', '')

      if (cleanHex.length === 3) {
        // 3자리 hex (#RGB)
        r = parseInt(cleanHex[0] + cleanHex[0], 16)
        g = parseInt(cleanHex[1] + cleanHex[1], 16)
        b = parseInt(cleanHex[2] + cleanHex[2], 16)
      } else if (cleanHex.length === 6) {
        // 6자리 hex (#RRGGBB)
        r = parseInt(cleanHex.substring(0, 2), 16)
        g = parseInt(cleanHex.substring(2, 4), 16)
        b = parseInt(cleanHex.substring(4, 6), 16)
      } else {
        // 잘못된 형식인 경우 기본값 사용
        console.warn('Invalid color format:', color)
        r = g = b = 0
      }
    }

    const rgbaResult = `rgba(${r}, ${g}, ${b}, ${alpha})`
    return rgbaResult
  }

  /**
   * 파동 애니메이션을 제거하는 함수
   */
  public removeShapeAnimation(): void {
    // 애니메이션 프레임 정리
    if (this.rippleAnimationId) {
      cancelAnimationFrame(this.rippleAnimationId)
      this.rippleAnimationId = undefined
    }

    // 파동 원들 제거
    this.rippleAnimations.forEach((ripple: IRing) => {
      ripple.destroy()
    })
    this.rippleAnimations = []

    // systemLayer 다시 그리기
    const systemLayer = this.getSystemLayer()
    if (systemLayer) {
      systemLayer.batchDraw()
    }
  }

  /**
   * 파동 애니메이션 위치를 업데이트하는 함수 (balloon 이동 시 사용)
   */
  public updateRipplePosition(): void {
    if (!this.entity.balloon || this.rippleAnimations.length === 0) return

    const balloonPos = this.entity.balloon.getPosition()
    const balloonCenter = {
      x: this.entity.node.x() + balloonPos.x,
      y: this.entity.node.y() + balloonPos.y,
    }

    // 모든 파동 원의 위치를 balloon 중심으로 업데이트
    this.rippleAnimations.forEach((ripple: IRing) => {
      ripple.x(balloonCenter.x)
      ripple.y(balloonCenter.y)
    })

    const systemLayer = this.getSystemLayer()
    if (systemLayer) {
      systemLayer.batchDraw()
    }
  }

  /**
   * balloon이 항상 파동보다 위에 있도록 보장하는 함수
   */
  public ensureBalloonAboveRipples(): void {
    if (!this.entity.balloon || this.rippleAnimations.length === 0) return

    // balloon과 text를 맨 위로 이동
    this.entity.balloon.moveToTop()

    // 현재 balloon의 zIndex 가져오기
    const balloonZIndex = this.entity.balloon.head.rect.zIndex()

    // 모든 파동들을 balloon보다 2단계 아래에 배치
    this.rippleAnimations.forEach((ripple: IRing, index: number) => {
      ripple.zIndex(Math.max(0, balloonZIndex - (3 + index))) // 파동들이 겹치지 않도록 순차적으로 배치
    })
  }
}
